import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Package, XCircle, TrendingDown, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export type CartConflict = {
  removed: Array<{
    cartItemId: string;
    modelId: string;
    modelName: string;
    brand: string;
    imageUrl?: string;
    grade: string;
    quantity: number;
    serialNumber?: string;
    warehouseCode?: string;
    warehouseLocation?: string;
    reason: string;
  }>;
  adjusted: Array<{
    cartItemId: string;
    modelId: string;
    modelName: string;
    brand: string;
    imageUrl?: string;
    grade: string;
    oldQuantity: number;
    newQuantity: number;
    warehouseCode?: string;
    warehouseLocation?: string;
    reason: string;
  }>;
};

interface CartConflictsModalProps {
  conflicts: CartConflict | null;
  onClose: () => void;
  onAcknowledge?: () => void;
}

export default function CartConflictsModal({ conflicts, onClose, onAcknowledge }: CartConflictsModalProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (conflicts && (conflicts.removed.length > 0 || conflicts.adjusted.length > 0)) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [conflicts]);

  if (!conflicts) return null;

  // Group items by model
  const groupedConflicts = new Map<
    string,
    {
      modelId: string;
      modelName: string;
      brand: string;
      imageUrl?: string;
      removed: typeof conflicts.removed;
      adjusted: typeof conflicts.adjusted;
    }
  >();

  conflicts.removed.forEach((item) => {
    const key = item.modelId;
    if (!groupedConflicts.has(key)) {
      groupedConflicts.set(key, {
        modelId: item.modelId,
        modelName: item.modelName,
        brand: item.brand,
        imageUrl: item.imageUrl,
        removed: [],
        adjusted: [],
      });
    }
    groupedConflicts.get(key)!.removed.push(item);
  });

  conflicts.adjusted.forEach((item) => {
    const key = item.modelId;
    if (!groupedConflicts.has(key)) {
      groupedConflicts.set(key, {
        modelId: item.modelId,
        modelName: item.modelName,
        brand: item.brand,
        imageUrl: item.imageUrl,
        removed: [],
        adjusted: [],
      });
    }
    groupedConflicts.get(key)!.adjusted.push(item);
  });

  const totalRemoved = conflicts.removed.length;
  const totalAdjusted = conflicts.adjusted.length;

  const handleClose = () => {
    setOpen(false);
    onClose();
    if (onAcknowledge) {
      onAcknowledge();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="dialog-cart-conflicts">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          data-testid="button-close-conflicts-x"
        >
          <XCircle className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Cart Items Updated
          </DialogTitle>
          <DialogDescription>
            Some items in your cart were unavailable or had limited stock. Your cart has been automatically updated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Summary */}
          <div className="flex gap-4">
            {totalRemoved > 0 && (
              <Badge variant="destructive" className="text-sm" data-testid="badge-removed-count">
                <XCircle className="h-4 w-4 mr-1" />
                {totalRemoved} {totalRemoved === 1 ? "item" : "items"} removed
              </Badge>
            )}
            {totalAdjusted > 0 && (
              <Badge variant="secondary" className="text-sm" data-testid="badge-adjusted-count">
                <TrendingDown className="h-4 w-4 mr-1" />
                {totalAdjusted} {totalAdjusted === 1 ? "item" : "items"} adjusted
              </Badge>
            )}
          </div>

          {/* Grouped by Model */}
          {Array.from(groupedConflicts.values()).map((group, index) => (
            <Card key={group.modelId} data-testid={`conflict-model-${group.modelId}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  {group.imageUrl ? (
                    <img 
                      src={group.imageUrl} 
                      alt={group.modelName}
                      className="w-16 h-16 object-contain rounded flex-shrink-0"
                    />
                  ) : (
                    <Package className="h-16 w-16 p-3 text-muted-foreground flex-shrink-0 bg-muted rounded" />
                  )}
                  <div className="flex-1 space-y-3">
                    <div>
                      <p className="font-semibold">{group.brand}</p>
                      <p className="text-sm text-muted-foreground">{group.modelName}</p>
                    </div>

                    {/* Removed Items */}
                    {group.removed.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-destructive flex items-center gap-2">
                          <XCircle className="h-4 w-4" />
                          Removed from Cart
                        </p>
                        {group.removed.map((item) => (
                          <div
                            key={item.cartItemId}
                            className="ml-6 p-3 bg-destructive/5 border border-destructive/20 rounded-md"
                            data-testid={`removed-item-${item.cartItemId}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm font-medium">
                                  Grade {item.grade} {item.serialNumber ? `(S/N: ${item.serialNumber})` : ""}
                                </p>
                                {!item.serialNumber && (
                                  <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                                )}
                                {item.warehouseCode && item.warehouseLocation && (
                                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                    <MapPin className="h-3 w-3" />
                                    {item.warehouseCode} | {item.warehouseLocation}
                                  </div>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-destructive mt-2">{item.reason}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Adjusted Items */}
                    {group.adjusted.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-orange-600 dark:text-orange-400 flex items-center gap-2">
                          <TrendingDown className="h-4 w-4" />
                          Quantity Reduced
                        </p>
                        {group.adjusted.map((item) => (
                          <div
                            key={item.cartItemId}
                            className="ml-6 p-3 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-md"
                            data-testid={`adjusted-item-${item.cartItemId}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm font-medium">Grade {item.grade}</p>
                                <p className="text-sm text-muted-foreground">
                                  {item.oldQuantity} → {item.newQuantity} units
                                </p>
                                {item.warehouseCode && item.warehouseLocation && (
                                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                    <MapPin className="h-3 w-3" />
                                    {item.warehouseCode} | {item.warehouseLocation}
                                  </div>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">{item.reason}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-end mt-6">
          <Button onClick={handleClose} data-testid="button-acknowledge-conflicts">
            I Understand
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
